import React, { useRef, useState, useContext, useEffect, useCallback } from 'react';
import { classNames } from 'primereact/utils';
import { InputNumber } from 'primereact/inputnumber';
import { useHistory, useParams, useLocation } from "react-router-dom";
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { BrCalendar } from './BrCalendar';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { ErrorValidationMessage } from './ErrorValidationMessage';
import { AuthContext } from '../contexts/AuthContext'
import api from '../api/api';
import '../layout/sass/_form.scss';
import { TabelaEditarNotas } from './TabelaEditarNotas';
import { Dropdown } from 'primereact/dropdown';
import { TabelaAdicionarNotas } from './TabelaAdicionarNotas';
// Novas Importações
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';

export const FormAvaliacao = ({ isEditing, isLoading, defaultValues }) => {    

    const history = useHistory();
    const location = useLocation();
    const toast = useRef(null);
    const [isSubmiting, setIsSubmiting] = useState(false)
    const { user } = useContext(AuthContext);
    const [turma, setTurma] = useState("");
    const { id } = useParams();
    const [turmaTemplate, setTurmaTemplate] = useState("");
    const [alunos, setAlunos] = useState([])

    const turmaIdContexto = location.state?.turmaId;

    // Novos estados para o Período Letivo
    const [periodos, setPeriodos] = useState([]);

    // --- INICIO DA NOVA FUNCIONALIDADE: TIPOS DE AVALIAÇÃO ---
    const [tiposOptions, setTiposOptions] = useState([]);
    const [showNewTipoDialog, setShowNewTipoDialog] = useState(false);
    const [newTipoDescricao, setNewTipoDescricao] = useState('');

    const [ano, setAno] = useState(new Date().getFullYear());
    const [anos, setAnos] = useState([]);

    const schema = Yup.object().shape({
        turmaId: Yup
            .number()
            .positive("A turma deve ser selecionado")
            .integer("A turma deve estar na lista"),
        data: Yup
            .date()
            .typeError("Uma data deve ser inserida")
            .required("A data é obrigatória"),
        tipo: Yup
            .string()
            .required("O tipo de avaliação é obrigatório"),
        periodoLetivo: Yup
            .string()
            .required("O período letivo é obrigatório"),
        notaTotal: Yup
            .string()
            .required("A nota total é obrigatório"),
    })

    // INICIALIZAÇÃO DO FORMIK MOVIDA PARA CIMA DAS FUNÇÕES QUE O UTILIZAM
    const formik = useFormik({
        initialValues: {
            ...defaultValues,

            turmaId: defaultValues?.turmaId || turmaIdContexto || null,

            // Se defaultValues já tem periodoLetivo como "B_03", ele assume aqui
            periodoLetivo: defaultValues?.periodoLetivo || defaultValues?.periodo_letivo || null,
            
            tipo: typeof defaultValues?.tipo === 'string' 
                    ? defaultValues.tipo 
                    : (defaultValues?.nomeTipoExibicao || null),
        },
        validationSchema: schema,
        enableReinitialize: true,
        onSubmit: async (data) => {
            setIsSubmiting(true)
            try {
                if (!isEditing) {
                    const dataAvaliacao = await api.post("avaliacao", data)
                    
                    // Alterado para for...of para garantir sincronismo e evitar tela branca
                    for (const item of alunos) {
                        const data1 = {
                            matriculaId: item.id,
                            avaliacaoId: dataAvaliacao.data.id,
                            notaAluno: item.notaAluno === undefined ? 0 : item.notaAluno
                        }
                        await api.post("nota", data1);
                    }

                    formik.resetForm()
                    toast.current.show({ life: 2000, severity: 'success', summary: 'Cadastro concluído!', detail: `Adicionado com sucesso` });
                    
                    setTimeout(async () => {
                        // Alterado para for...of para garantir que as notas finais sejam processadas
                        for (const item of alunos) {
                            await setNotaFinal(item.id);
                        }
                        
                        // Redirecionamento condicional baseado no contexto da turma
                        if (turmaIdContexto) {
                            history.push(`/turmas/edit/${turmaIdContexto}`);
                        } else {
                            history.push('/turmas');
                        }
                    }, 2000)
                }
                else {
                    let result = await api.get(`avaliacao/${data.id}`);
                    let nota = result.data.nota;
                    let data1 = {
                        id: data.id,
                        data: data.data,
                        tipo: data.tipo,
                        // AJUSTE AQUI: Enviar com o nome novo para o DTO do Back-end
                        periodoLetivo: data.periodoLetivo, 
                        nota: nota,
                        notaTotal: data.notaTotal
                    }
                    await api.put("avaliacao", data1)

                    toast.current.show({ life: 2000, severity: 'success', summary: 'Cadastro atualizado!', detail: `Avaliação atualizada com sucesso` });
                    setTimeout(() => {
                        if (turmaIdContexto) {
                            history.push(`/turmas/edit/${turmaIdContexto}`);
                        } else {
                            history.push('/turmas');
                        }
                    }, 2000)
                }
            }
            catch (error) {
                if (!isEditing)
                    toast.current.show({ life: 2000, severity: 'error', summary: 'Erro ao cadastrar avaliação!', detail: error.toString() });
                else
                    toast.current.show({ life: 2000, severity: 'error', summary: 'Erro ao editar avaliação!', detail: error.toString() });
            }
            setIsSubmiting(false)
        }
    })

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);

    const getTiposAvaliacao = useCallback(async () => {
        try {
            const response = await api.get("avaliacao/tipoAvaliacao");
            const dadosApi = response.data.map(t => ({ 
                label: t.label || t.descricao || t, 
                value: t.value || t.descricao || t 
            }));

            // Se o que vinha no formik antes eram apenas Strings, precisamos mapear para Objetos também
            const itensOriginais = (formik.values.tipoAvaliacao || []).map(t => 
                typeof t === 'string' ? { label: t, value: t } : t
            );

            // Remove duplicados baseados no value
            const listaUnica = [...new Map([...itensOriginais, ...dadosApi].map(item => [item['value'], item])).values()];
            
            setTiposOptions(listaUnica);
        } catch (error) {
            if (formik.values.tipoAvaliacao) {
                const fallback = formik.values.tipoAvaliacao.map(t => 
                    typeof t === 'string' ? { label: t, value: t } : t
                );
                setTiposOptions(fallback);
            }
        }
    }, [formik.values.tipoAvaliacao]);

    const handleCreateTipo = async () => {
        if (!newTipoDescricao.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Aviso', detail: 'Informe a descrição do tipo', life: 2000 });
            return;
        }
        try {
            const response = await api.post("avaliacao/tipoAvaliacao", { descricao: newTipoDescricao });
            const novo = response.data;
            
            setTiposOptions(prev => [...prev, { label: novo.descricao, value: novo.descricao }]);
            formik.setFieldValue("tipo", novo.descricao);
            
            setShowNewTipoDialog(false);
            setNewTipoDescricao('');
            toast.current.show({ severity: 'success', summary: 'Sucesso', detail: 'Novo tipo cadastrado', life: 2000 });
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Erro', detail: 'Falha ao salvar novo tipo' });
        }
    };

    const handleTurmaClick = async (e) => {
        try {
            // Se o usuário limpar o dropdown (clicar no X), e.value será null
            if (!e.value) {
                formik.setFieldValue("turmaId", null);
                setAlunos([]); // Limpa a lista de alunos se a turma for removida
                return;
            }

            formik.setFieldValue("turmaId", e.value);
            const response = await api.get(`matricula/turma/${e.value}`);
            const itens = response.data.map((i) => { 
                return { id: i.id, matriculaNome: i.aluno.nome, notaAluno: i.notaAluno } 
            });
            setAlunos(itens);
        }
        catch (error) {
            toast.current.show({ 
                severity: 'error', 
                summary: 'Erro ao obter alunos', 
                detail: error.toString() 
            });
        }
    }

    const setNotaFinal = async (matriculaId) => {
        try {
            const response = await api.get(`nota/soma/${matriculaId}`);
            let tempMatricula = {
                'id': response.data.matricula.id,
                'alunoId': response.data.matricula.aluno.id,
                'conclusao': response.data.matricula.conclusao,
                'cpf': response.data.matricula.aluno.cpf,
                'dataMatricula': response.data.matricula.dataMatricula,
                'turmaId': response.data.matricula.turma.id,
                'instrumentoId': response.data.matricula.instrumento.id,
                'notaFinal': response.data.notaTotal,
                'status': response.data.matricula.status,
                'periodoId': response.data.matricula.periodoId
            }
            await api.put("matricula", tempMatricula)
        }
        catch (error) {
            toast.current.show({ severity: 'error', summary: 'Erro ao atualizar nota', detail: error.toString() });
        }
    }

    const getAnos =  useCallback(() => {
        let tempAnos = [];
        let anoInicial = ano - 5
        let anoFinal = ano + 1
        for (let tempAno = anoInicial; tempAno <= anoFinal; tempAno++) {
            tempAnos.push({ label: tempAno.toString(), value: tempAno});
        }
        setAnos(tempAnos)        
    }, [ano]);

    const getPeriodos = useCallback(async () => {
        try {
            const response = await api.get("avaliacao/periodoLetivo");
            
            setPeriodos(response.data.map(p => {
                return { label: p.label, value: p.value };
            }));
        } catch (error) {
            console.error("Erro ao carregar períodos letivos", error);
        }
    }, []);

    const handleAnoclick = (e) => {
        if (e.value !== null) {                      
            setAno(e.value)            
        }
    }

    const getTurma = useCallback(async () => {
        try {
            if (user.perfilUser[0] !== 'user-instrutor') {
                const response = await api.get(`turma/turmaAllAno/${ano}`);
                setTurma(response.data.map(turma => {
                    var turm = { label: '', value: '' };
                    turm.label = turma.ano + " - " + turma.cursoNome + " - " + turma.diaSemana + "  " + turma.hora
                    turm.value = turma.id
                    return turm
                }))
            } else {
                const response = await api.get(`turma/turmaAllAtualInstrutor/${user.email}`);
                setTurma(response.data.map(turma => {
                    var turm = { label: '', value: '' };
                    turm.label = turma.ano + " - " + turma.cursoNome + " - " + turma.diaSemana + "  " + turma.hora
                    turm.value = turma.id
                    return turm
                }))
            }

            if (isEditing && id) {
                const response = await api.get(`nota/avaliacao/${id}`);
                setTurmaTemplate(response.data[0].matricula.turma.ano + " - " + response.data[0].matricula.turma.curso.nome + " - " + response.data[0].matricula.turma.diaSemana + "  " + response.data[0].matricula.turma.hora)
            }
        }
        catch (error) {
            toast.current.show({ severity: 'error', summary: 'Erro ao obter turmas', detail: error.toString() });
        }
    }, [ano, user, isEditing, id]);

    // Efeito para carregar alunos se a turma vier travada pelo contexto
    useEffect(() => {
        if (!isEditing && turmaIdContexto) {
            const carregarAlunosAutomatico = async () => {
                try {
                    const response = await api.get(`matricula/turma/${turmaIdContexto}`);
                    const itens = response.data.map((i) => ({ 
                        id: i.id, 
                        matriculaNome: i.aluno.nome, 
                        notaAluno: i.notaAluno 
                    }));
                    setAlunos(itens);
                } catch (error) {
                    console.error("Erro ao carregar alunos da turma fixa", error);
                }
            };
            carregarAlunosAutomatico();
        }
    }, [turmaIdContexto, isEditing]);

    useEffect(() => {
            getTurma();
    }, [getTurma])

    useEffect(() => {
        getAnos();
        getPeriodos();
        if (formik.values.tipoAvaliacao || isEditing) {
            getTiposAvaliacao();
        }
    }, [getAnos, getPeriodos, getTiposAvaliacao, isEditing]);

    // Localize seus useEffects e adicione este:
    useEffect(() => {
        if (isEditing && defaultValues && defaultValues.data) {
            // Extrai o ano da data da avaliação que está sendo editada
            const dataAvaliacao = new Date(defaultValues.data);
            const anoAvaliacao = dataAvaliacao.getFullYear();
            
            if (!isNaN(anoAvaliacao)) {
                setAno(anoAvaliacao);
            }
        }
    }, [isEditing, defaultValues]);

    return (
        <>
            <div className="card p-grid p-col-12 p-mx-0 p-mt-0">
                <Toast ref={toast} position="bottom-right" />
                <div className="p-col-12">
                    <h2 className="p-mt-0">Avaliação</h2>
                    <form onSubmit={formik.handleSubmit}>
                        <div className="p-formgrid p-grid p-fluid p-mx-0">
                            <div className="p-field p-col-12 p-sm-2">
                                <label
                                    htmlFor="turmaId"
                                    className={classNames({ 'p-error': isFormFieldValid('turmaId') })}
                                >
                                    Ano
                                </label>
                                {
                                    !isLoading ?
                                        <Dropdown
                                            id="turmaId"
                                            name="turmaId"
                                            options={anos}
                                            value={ano}
                                            onChange={(e) => handleAnoclick(e)}
                                            placeholder={isEditing ? ano : "Selecione um Ano"}
                                            disabled={isEditing}
                                        />
                                        :
                                        <Skeleton height="35px" />
                                }
                                {formik.errors.turmaId && formik.touched.turmaId &&
                                    <ErrorValidationMessage message={formik.errors.turmaId} />
                                }
                            </div>
                            <div className="p-field p-col-12 p-sm-3">
                                <label
                                    htmlFor="turmaId"
                                    className={classNames({ 'p-error': isFormFieldValid('turmaId') })}
                                >
                                    Turma
                                </label>
                                {
                                    !isLoading ?
                                        <Dropdown
                                            id="turmaId"
                                            name="turmaId"
                                            options={turma}
                                            emptyMessage={"Nenhuma turma encontrada"}
                                            emptyFilterMessage={"Nenhuma turma encontrado"}
                                            filter={true}
                                            value={formik.values.turmaId}
                                            onChange={(e) => handleTurmaClick(e)}
                                            className={classNames({ 'p-invalid': isFormFieldValid('turmaId') })}
                                            placeholder={isEditing ? turmaTemplate : "Selecione uma turma"}
                                            showClear={true}
                                            disabled={isEditing || !!turmaIdContexto}
                                        />
                                        :
                                        <Skeleton height="35px" />
                                }
                                {formik.errors.turmaId && formik.touched.turmaId &&
                                    <ErrorValidationMessage message={formik.errors.turmaId} />
                                }
                            </div>
                            <div className="p-field p-col-12 p-sm-4 p-md-2">
                                <label
                                    htmlFor="data"
                                    className={classNames({ 'p-error': isFormFieldValid('data') })}
                                >
                                    Data
                                </label>
                                <BrCalendar
                                    id="data"
                                    name="data"
                                    value={formik.values.data}
                                    onChange={formik.handleChange}
                                    placeholder={isEditing ? new Date(formik.values.data).toLocaleDateString('pt-BR') : ""}
                                    className={classNames({ 'p-invalid': isFormFieldValid('data') })}
                                    yearRange={`2000:${new Date().getFullYear()}`}
                                />
                                {formik.errors.data && formik.touched.data &&
                                    <ErrorValidationMessage message={formik.errors.data} />
                                }
                            </div>

                            <div className="p-field p-col-12 p-sm-3">
                                <label
                                    htmlFor="tipo"
                                    className={classNames({ 'p-error': isFormFieldValid('tipo') })}
                                >
                                    Tipo de Avaliação
                                </label>
                                {
                                    !isLoading ?
                                        <div className="p-d-flex p-ai-center">
                                            <Dropdown
                                            id="tipo"
                                            name="tipo"
                                            value={formik.values.tipo}
                                            options={tiposOptions}
                                            filter={true}
                                            
                                            // ESTAS DUAS LINHAS RESOLVEM O ERRO:
                                            optionLabel="label"  // O que aparece para o usuário clicar
                                            optionValue="value"  // O que será salvo no banco (o texto da descrição)
                                            
                                            onChange={(e) => formik.setFieldValue("tipo", e.value)}
                                            className={classNames({ 'p-invalid': isFormFieldValid('tipo') }, "p-mr-2")}
                                            placeholder="Selecione o tipo"
                                            showClear={true}
                                            style={{ flex: 1 }}
                                        />
                                            <Button
                                                type="button"
                                                icon="pi pi-plus"
                                                className="p-button-rounded p-button-success p-button-outlined"
                                                onClick={() => setShowNewTipoDialog(true)}
                                                tooltip="Novo Tipo"
                                            />
                                        </div>
                                        :
                                        <Skeleton height="35px" />
                                }

                                {formik.errors.tipo && formik.touched.tipo &&
                                    <ErrorValidationMessage message={formik.errors.tipo} />
                                }
                            </div>

                            <div className="p-field p-col-12 p-sm-2">
                                <label
                                    htmlFor="periodoLetivo"
                                    className={classNames({ 'p-error': isFormFieldValid('periodoLetivo') })}
                                >
                                    Período Letivo
                                </label>
                                {
                                    !isLoading ?
                                        <Dropdown
                                            id="periodoLetivo"
                                            name="periodoLetivo"
                                            value={formik.values.periodoLetivo}
                                            options={periodos}
                                            optionLabel="label"
                                            optionValue="value"
                                            onChange={(e) => formik.setFieldValue("periodoLetivo", e.value)}
                                            placeholder="Selecione o período"
                                            className={classNames({ 'p-invalid': isFormFieldValid('periodoLetivo') })}
                                            showClear={true}
                                        />
                                        :
                                        <Skeleton height="35px" />
                                }

                                {formik.errors.periodoLetivo && formik.touched.periodoLetivo &&
                                    <ErrorValidationMessage message={formik.errors.periodoLetivo} />
                                }
                            </div>

                            <div className="p-field p-col-12 p-sm-1">
                                <label
                                    htmlFor="notaTotal"
                                    className={classNames({ 'p-error': isFormFieldValid('notaTotal') })}
                                >
                                    Nota Total
                                </label>
                                {
                                    !isLoading ?
                                        <InputNumber
                                            id="notaTotal"
                                            name="notaTotal"
                                            mode="decimal" minFractionDigits={2}
                                            className={classNames({ 'p-invalid': isFormFieldValid('notaTotal') })}
                                            onChange={(e) => formik.setFieldValue("notaTotal", e.value)}
                                            locale="pt-BR"
                                            value={formik.values.notaTotal}
                                        />
                                        :
                                        <Skeleton height="35px" />
                                }
                                {formik.errors.notaTotal && formik.touched.notaTotal &&
                                    <ErrorValidationMessage message={formik.errors.notaTotal} />
                                }
                            </div>
                        </div>

                        <div className="p-d-flex p-jc-end p-mr-1" >
                            {
                                !isEditing ?
                                    <TabelaAdicionarNotas alunos={alunos} setAlunos={setAlunos} avaliacao={formik.values.notaTotal} />
                                    :
                                    <></>
                            }
                        </div>

                        <div className="p-d-flex p-jc-end p-mr-1 p-mt-3">
                            {
                                !isLoading ?
                                    <Button
                                        label={isEditing ? 'Atualizar' : 'Salvar'}
                                        type="submit"
                                        iconPos="right"
                                        loading={isSubmiting}
                                    />
                                    :
                                    <Skeleton width="82.16px" height="35px" />
                            }
                        </div>
                    </form>
                    <div className="p-col-12">
                        {
                            isEditing ?
                                <TabelaEditarNotas
                                    avaliacaoId={defaultValues.id}
                                    avaliacao={formik.values.notaTotal}
                                />
                                :
                                <></>
                        }
                    </div>
                </div>
            </div>

            <Dialog
                header="Novo Tipo de Avaliação"
                visible={showNewTipoDialog}
                style={{ width: '30vw' }}
                modal
                onHide={() => setShowNewTipoDialog(false)}
                footer={
                    <div>
                        <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={() => setShowNewTipoDialog(false)} />
                        <Button label="Salvar" icon="pi pi-check" onClick={handleCreateTipo} autoFocus />
                    </div>
                }
            >
                <div className="p-fluid">
                    <div className="p-field">
                        <label htmlFor="newTipoDesc">Descrição do Tipo</label>
                        <InputText 
                            id="newTipoDesc" 
                            value={newTipoDescricao} 
                            onChange={(e) => setNewTipoDescricao(e.target.value)} 
                            placeholder="Ex: Prova Mensal, Trabalho..."
                        />
                    </div>
                </div>
            </Dialog>
        </>
    );
}